/**
 * @jsxRuntime classic
 * @jsx jsx
 */
import React, { useCallback, useMemo } from 'react';

import { useIntl } from 'react-intl';

import { jsx } from '@atlaskit/css';
import ViewAllIcon from '@atlaskit/icon/core/align-text-left';
import { fg } from '@atlaskit/platform-feature-flags';
import {
	AgentProfileCardTrigger as AgentProfileCardTriggerLegacy,
	ProfileClient,
} from '@atlaskit/profilecard';
import { AgentNameSideNav } from '@atlaskit/rovo-agent-components/agent-name-side-nav';
import { AgentVerifiedIcon } from '@atlaskit/rovo-agent-components/agent-verified';
import type { AgentCreatorType } from '@atlaskit/rovo-agent-components/common/types';
import { isForgeAgentByCreatorType } from '@atlaskit/rovo-agent-components/common/utils/is-forge-agent';
import { AgentAvatar } from '@atlaskit/rovo-agent-components/ui/AgentAvatar';
import { AgentMenuItemSkeleton } from '@atlaskit/rovo-agent-components/ui/AgentMenuItemSkeleton';
import { token } from '@atlaskit/tokens';
import { AgentProfileCardWithTrigger } from '@atlassian/agent-profile-card/card-with-trigger';
import type { Agent } from '@atlassian/conversation-assistant-service-api/types/assistance-service';

import type { AgentsListProps } from '../../../common/types';
import { LinkMenuItem } from '../../../common/ui/link-menu-item';
import { ListItem } from '../../../common/ui/list-item';
import { MenuItemBase } from '../../../common/ui/menu-item';

import messages from './messages';

export const isForgeAgent = (agent: Agent): boolean =>
	agent.creator_type === 'THIRD_PARTY' || agent.creator_type === 'FORGE';

const AGENTS_LIMIT = 5;

export const AgentsMenuItems = ({
	agents = [],
	isFetchingRecommendedAgents,
	onViewAllAgentsClick,
	showBrowseAgents = true,
	...props
}: AgentsListProps & {
	cloudId: string;
	isADSNavigationEnabled: boolean;
	showBrowseAgents?: boolean;
}): JSX.Element => {
	const { formatMessage } = useIntl();
	const hasAgents = agents.length > 0;

	const limitedAgents = agents.slice(0, AGENTS_LIMIT);

	const onViewAll = useCallback(
		(e: React.MouseEvent) => {
			e.preventDefault();
			onViewAllAgentsClick?.();
		},
		[onViewAllAgentsClick],
	);

	return (
		<React.Fragment>
			{hasAgents
				? limitedAgents.map((agent) => <AgentMenuItem {...props} agent={agent} key={agent.id} />)
				: null}
			{isFetchingRecommendedAgents &&
				Array(Math.max(0, AGENTS_LIMIT - limitedAgents.length))
					.fill(null)
					.map((_, index) => (
						<AgentMenuItemSkeleton
							index={index}
							skeletonAvatarSize="small"
							paddingHorizontal={token('space.050')}
						/>
					))}
			{showBrowseAgents && (
				<LinkMenuItem
					isADSNavigationEnabled={props.isADSNavigationEnabled}
					onClick={onViewAll}
					href="#"
					elemBefore={<ViewAllIcon label="" />}
				>
					{formatMessage(messages.viewAllAgentsLabel)}
				</LinkMenuItem>
			)}
		</React.Fragment>
	);
};

type AgentMenuItemProps = Omit<AgentsListProps, 'agents'> & {
	agent: Agent;
	cloudId: string;
	isADSNavigationEnabled: boolean;
};

const AgentMenuItem = ({
	agent,
	onSelectAgent,
	selectedAgent,
	cloudId,
	isADSNavigationEnabled,
	onConversationStarterClick,
	onDeleteAgent,
}: AgentMenuItemProps) => {
	const profileClient = useMemo(
		() =>
			new ProfileClient({
				url: '/gateway/api/directory/graphql',
				productIdentifier: 'rovo',
				cloudId,
			}),
		[cloudId],
	);

	const onAgentItemClick = useCallback(
		(event: React.MouseEvent) => {
			event.preventDefault();
			onSelectAgent(agent, isADSNavigationEnabled);
		},
		[onSelectAgent, agent, isADSNavigationEnabled],
	);

	const isSelected = agent.id === selectedAgent?.id;

	const profileCardTriggerProps = {
		trigger: 'hover' as const,
		agentId: agent.id,
		identityAccountId:
			agent.identity_account_id && fg('rovo_agent_profile_card_relay')
				? agent.identity_account_id
				: '',
		placement: fg('rovo_agent_profile_card_relay') ? ('left' as const) : undefined,
		resourceClient: profileClient,
		onConversationStartersClick: onConversationStarterClick
			? (starter: Parameters<NonNullable<typeof onConversationStarterClick>>[1]) =>
					onConversationStarterClick(agent, starter, isADSNavigationEnabled)
			: undefined,
		onChatClick: onAgentItemClick,
		cloudId,
		onDeleteAgent,
	};

	const AgentProfileCardTrigger = fg('rovo_agent_profile_card_relay')
		? AgentProfileCardWithTrigger
		: AgentProfileCardTriggerLegacy;

	const elemBefore = (
		<AgentAvatar
			agentNamedId={agent.external_config_reference ?? agent.named_id}
			agentId={agent.id}
			agentIdentityAccountId={agent.identity_account_id}
			size="small"
			showBorder={false}
			isForgeAgent={
				fg('rovo_agent_support_a2a_avatar')
					? isForgeAgentByCreatorType(agent.creator_type as AgentCreatorType)
					: isForgeAgent(agent)
			}
			forgeAgentIconUrl={agent.icon}
		/>
	);

	// When ADS navigation is enabled, use the standard LinkMenuItem structure
	if (isADSNavigationEnabled) {
		return (
			<AgentProfileCardTrigger {...profileCardTriggerProps}>
				<LinkMenuItem
					isADSNavigationEnabled={isADSNavigationEnabled}
					key={agent.id}
					href="#"
					isSelected={isSelected}
					onClick={onAgentItemClick}
					elemBefore={elemBefore}
				>
					{fg('rovo_agents_agent_verification') ? <AgentName agent={agent} /> : agent.name}
				</LinkMenuItem>
			</AgentProfileCardTrigger>
		);
	}

	// When not using ADS navigation, wrap AgentProfileCardTrigger inside ListItem
	// to ensure the profile card trigger's wrapper span is inside the listitem,
	// not a direct child of the list (which would violate ARIA list semantics).
	// We also disable ARIA attributes on the trigger's wrapper span because
	// aria-expanded is not allowed on span elements.
	return (
		<ListItem>
			<AgentProfileCardTrigger {...profileCardTriggerProps} disabledAriaAttributes>
				<MenuItemBase
					href="#"
					isSelected={isSelected}
					onClick={onAgentItemClick}
					elemBefore={elemBefore}
					level={1}
				>
					{fg('rovo_agents_agent_verification') ? <AgentName agent={agent} /> : agent.name}
				</MenuItemBase>
			</AgentProfileCardTrigger>
		</ListItem>
	);
};

const AgentName = ({ agent }: { agent: Agent }): JSX.Element => (
	<AgentNameSideNav name={agent.name}>
		{agent.is_verified ? <AgentVerifiedIcon /> : null}
	</AgentNameSideNav>
);
